/**
 * Cart drawer + cart page — WIL-211.
 * Section refresh after add/qty/remove, GST-aware shipping estimate, C&C attributes.
 */
(function () {
  const DRAWER_SECTION = 'cart-drawer';
  const COLLECT_STORAGE_KEY = 'mapCollectLocation';
  const COLLECT_COOKIE_KEY = 'selected_location';
  const COLLECT_DRAWER_ID = 'ClickCollectLocationDrawer';
  const SHIPPING_STORAGE_KEY = 'mapCartShippingEstimate';

  /** @returns {HTMLDialogElement | null} */
  function getDrawer() {
    const dialog = document.getElementById('CartDrawer');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /** @returns {string} */
  function shopRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  /** @param {HTMLDialogElement} dialog */
  function bindCloseButtons(dialog) {
    dialog.querySelectorAll('[data-map-drawer-close]').forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      if (btn.dataset.cartDrawerCloseBound === 'true') return;
      btn.dataset.cartDrawerCloseBound = 'true';
      btn.addEventListener('click', () => {
        if (window.MapUI?.closeDrawer) window.MapUI.closeDrawer(dialog);
        else if (dialog.open) dialog.close();
      });
    });
  }

  /** @param {number} itemCount */
  function updateHeaderCartCount(itemCount) {
    document.querySelectorAll('[data-map-drawer-open="CartDrawer"]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;

      let badge = trigger.querySelector('sup');
      if (itemCount > 0) {
        if (!badge) {
          badge = document.createElement('sup');
          trigger.appendChild(badge);
        }
        badge.textContent = String(itemCount);
      } else if (badge) {
        badge.remove();
      }
    });
  }

  /**
   * @param {string} html
   * @param {string} selector
   * @returns {Element | null}
   */
  /**
   * @param {string} html
   * @param {string} selector
   * @returns {Element | null}
   */
  function firstFromHtml(html, selector) {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.querySelector(selector);
  }

  /**
   * @param {HTMLElement | null | undefined} el
   * @returns {string}
   */
  function sectionIdFrom(el) {
    if (!(el instanceof HTMLElement)) return '';
    return el.dataset.sectionId || '';
  }

  /** Section ids to ask cart/change.js to re-render with the updated cart. */
  function sectionsToRender() {
    const ids = [];
    const drawerId = sectionIdFrom(getDrawer()) || DRAWER_SECTION;
    const page = document.querySelector('[data-cart-page]');
    const pageId = sectionIdFrom(page instanceof HTMLElement ? page : null);
    if (drawerId) ids.push(drawerId);
    if (pageId && !ids.includes(pageId)) ids.push(pageId);
    if (!ids.includes(DRAWER_SECTION)) ids.push(DRAWER_SECTION);
    return ids;
  }

  /**
   * @param {Record<string, string> | null | undefined} sections
   * @param {string} sectionId
   * @returns {string}
   */
  function renderedSection(sections, sectionId) {
    if (!sections || !sectionId) return '';
    const html = sections[sectionId];
    return typeof html === 'string' ? html : '';
  }

  /**
   * WIL-648: a follow-up GET /cart?sections= can return the full cart document
   * or a stale copy, so the page keeps the removed line. Prefer HTML that
   * arrived with cart/change.js, then a no-store section fetch.
   *
   * @param {string} sectionId
   * @param {'root' | 'cart'} pagePath
   * @returns {Promise<string>}
   */
  async function fetchSectionHtml(sectionId, pagePath) {
    if (!sectionId) return '';
    const root = shopRoot();
    const path = pagePath === 'cart' ? `${root}cart` : root;
    const stamp = Date.now();

    try {
      const response = await fetch(`${path}?sections=${encodeURIComponent(sectionId)}&_=${stamp}`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store',
      });
      if (response.ok && (response.headers.get('content-type') || '').includes('json')) {
        const data = await response.json();
        const html = data && data[sectionId];
        if (typeof html === 'string' && html) return html;
      }
    } catch {
      /* try the single-section endpoint */
    }

    try {
      const response = await fetch(`${path}?section_id=${encodeURIComponent(sectionId)}&_=${stamp}`, {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store',
      });
      if (!response.ok) return '';
      const text = await response.text();
      if (!text || /<!doctype|<html[\s>]/i.test(text)) return '';
      return text;
    } catch {
      return '';
    }
  }

  /**
   * @param {{ open?: boolean, sections?: Record<string, string> }} [options]
   * @returns {Promise<boolean>}
   */
  async function refreshCartDrawer(options = {}) {
    const dialog = getDrawer();
    if (!dialog) return false;

    const wasOpen = dialog.open;
    const shouldOpen = Boolean(options.open) || wasOpen;
    const sectionId = sectionIdFrom(dialog) || DRAWER_SECTION;

    try {
      let html =
        renderedSection(options.sections, sectionId) || renderedSection(options.sections, DRAWER_SECTION);
      if (!html) html = await fetchSectionHtml(sectionId, 'root');
      if (!html && sectionId !== DRAWER_SECTION) html = await fetchSectionHtml(DRAWER_SECTION, 'root');
      if (!html) return false;

      const newDialog =
        firstFromHtml(html, '#CartDrawer') ?? firstFromHtml(html, '[data-cart-drawer]');
      if (!(newDialog instanceof HTMLDialogElement)) return false;

      const currentInner = dialog.querySelector('[data-cart-drawer-inner]');
      const newInner = newDialog.querySelector('[data-cart-drawer-inner]');

      if (currentInner instanceof HTMLElement && newInner instanceof HTMLElement) {
        currentInner.replaceWith(newInner);
      }

      const keepOpen = wasOpen || dialog.classList.contains('is-open');
      dialog.className = newDialog.className;
      if (keepOpen) dialog.classList.add('is-open');
      bindCloseButtons(dialog);

      if (shouldOpen && !dialog.open) {
        if (window.MapUI?.openDrawer) {
          window.MapUI.openDrawer(dialog);
        } else {
          const trigger = document.querySelector('[data-map-drawer-open="CartDrawer"]');
          if (trigger instanceof HTMLElement) trigger.click();
          else dialog.showModal();
        }
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * @param {{ sections?: Record<string, string> }} [options]
   * @returns {Promise<boolean>} true when there is no cart page, or the page inner was replaced
   */
  async function refreshCartPage(options = {}) {
    const page = document.querySelector('[data-cart-page]');
    if (!(page instanceof HTMLElement)) return true;

    const sectionId = sectionIdFrom(page);
    const inner = page.querySelector('[data-cart-page-inner]');
    if (!sectionId || !(inner instanceof HTMLElement)) return false;

    try {
      let html = renderedSection(options.sections, sectionId);
      if (!html) html = await fetchSectionHtml(sectionId, 'cart');
      if (!html) return false;

      const nextInner = firstFromHtml(html, '[data-cart-page-inner]');
      if (!(nextInner instanceof HTMLElement)) return false;
      inner.replaceWith(nextInner);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * @param {{ key: string, quantity: number }[] | undefined} lines
   * @returns {boolean}
   */
  function cartPageInSync(lines) {
    const page = document.querySelector('[data-cart-page]');
    if (!(page instanceof HTMLElement) || !Array.isArray(lines)) return true;

    const rendered = [...page.querySelectorAll('[data-cart-line]')];
    if (rendered.length !== lines.length) return false;

    const expected = new Map(lines.map((line) => [line.key, line.quantity]));
    for (const line of rendered) {
      if (!(line instanceof HTMLElement)) return false;
      const key = line.dataset.lineKey || '';
      if (!expected.has(key)) return false;
      const input = line.querySelector('[data-cart-qty-input]');
      const qty = input instanceof HTMLInputElement ? Number.parseInt(input.value, 10) : NaN;
      if (qty !== expected.get(key)) return false;
    }
    return true;
  }

  /**
   * @param {number} [itemCount]
   * @param {{ openDrawer?: boolean, sections?: Record<string, string>, lines?: { key: string, quantity: number }[] }} [options]
   */
  async function refreshCartViews(itemCount, options = {}) {
    if (typeof itemCount === 'number') {
      updateHeaderCartCount(itemCount);
    }
    const pageUpdated = await refreshCartPage(options);
    await refreshCartDrawer({ open: Boolean(options.openDrawer), sections: options.sections });
    restoreShippingEstimates();
    // WIL-648: the drawer and icon were updating while /cart kept the removed line.
    if (!pageUpdated || !cartPageInSync(options.lines)) {
      window.location.assign(`${shopRoot()}cart`);
    }
  }

  /**
   * GST-inclusive money from the form's data attributes — maths lives once in
   * map-utils.js (WIL-461).
   * @param {number} cents
   * @param {HTMLElement} form
   */
  function formatMoney(cents, form) {
    return window.MapUtils.formatMoney(
      cents,
      window.MapUtils.gstRateFromDataset(form.dataset),
      form.dataset.moneyFormat
    );
  }

  /** @param {unknown} price */
  function ratePriceCents(price) {
    // shipping_rates.json returns prices in dollars (string today, but treat a
    // numeric value as dollars too — guessing cents from magnitude mispriced
    // whole-dollar amounts >= $100).
    const dollars =
      typeof price === 'number' && Number.isFinite(price) ? price : Number.parseFloat(String(price ?? ''));
    if (Number.isNaN(dollars)) return 0;
    return Math.round(dollars * 100);
  }

  /**
   * @param {string} key
   * @param {number} quantity
   * @param {{ openDrawer?: boolean }} [options]
   */
  async function changeLine(key, quantity, options = {}) {
    const forms = document.querySelectorAll('[data-cart-items]');
    forms.forEach((form) => {
      if (form instanceof HTMLElement) form.setAttribute('aria-busy', 'true');
    });

    try {
      const response = await fetch(`${shopRoot()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: sectionsToRender(),
          sections_url: `${shopRoot()}cart`,
        }),
      });
      if (!response.ok) {
        console.warn('cart/change.js failed', response.status);
        const firstForm = forms[0];
        const message =
          (firstForm instanceof HTMLElement && firstForm.dataset.updateError) ||
          'Could not update the cart. Please try again.';
        const toast = /** @type {{ show?: (msg: string) => void } | undefined} */ (window.MapToast);
        toast?.show?.(message);
        return;
      }

      const cart = await response.json();
      const sections =
        cart.sections && typeof cart.sections === 'object' ? cart.sections : undefined;
      const lines = Array.isArray(cart.items)
        ? cart.items
            .filter((item) => item && typeof item.key === 'string')
            .map((item) => ({ key: item.key, quantity: Number(item.quantity) || 0 }))
        : undefined;
      await refreshCartViews(cart.item_count, {
        openDrawer: options.openDrawer === true || getDrawer()?.open === true,
        sections,
        lines,
      });
    } finally {
      document.querySelectorAll('[data-cart-items]').forEach((form) => {
        form.removeAttribute('aria-busy');
      });
    }
  }

  /** @returns {{ id: string, name: string, addressLine: string } | null} */
  function readStoredCollectLocation() {
    try {
      const raw = window.localStorage.getItem(COLLECT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.id === 'string') {
          return {
            id: parsed.id,
            name: parsed.name || parsed.pdpLabel || '',
            // Match the "address postcode" shape click-collect.js writes so the
            // attribute restore doesn't flip-flop the cart attribute value.
            addressLine: [parsed.addressLine || parsed.address, parsed.postcode].filter(Boolean).join(' '),
          };
        }
      }
    } catch {
      /* ignore */
    }

    const raw = window.MapUtils.readCookie(COLLECT_COOKIE_KEY);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      const id = parsed.location || parsed.id || '';
      const name = parsed.name || '';
      const address = parsed.addressLine || parsed.address || parsed.address_line_1 || '';
      const addressLine = [address, parsed.postcode].filter(Boolean).join(' ');
      if (!id && !name) return null;
      return { id: String(id), name: String(name), addressLine: String(addressLine) };
    } catch {
      return null;
    }
  }

  /**
   * @param {{ id: string, name: string, addressLine: string } | null} store
   */
  async function syncCollectAttributes(store) {
    const attributes = store
      ? {
          clickNcollect: store.id,
          'Pickup Location': store.name,
          'Pickup Address': store.addressLine,
        }
      : {
          clickNcollect: '',
          'Pickup Location': '',
          'Pickup Address': '',
        };

    const response = await fetch(`${shopRoot()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (!response.ok) return null;
    return response.json();
  }

  function paintPickupFromStore() {
    const store = readStoredCollectLocation();
    document.querySelectorAll('[data-cart-items]').forEach((form) => {
      if (!(form instanceof HTMLElement)) return;

      const idInput = form.querySelector('[data-cart-attr-id]');
      const nameInput = form.querySelector('[data-cart-attr-name]');
      const addressInput = form.querySelector('[data-cart-attr-address]');
      const panel = form.querySelector('[data-cart-pickup]');
      const nameEl = form.querySelector('[data-cart-pickup-name]');
      const addressEl = form.querySelector('[data-cart-pickup-address]');

      if (idInput instanceof HTMLInputElement) idInput.value = store?.id || '';
      if (nameInput instanceof HTMLInputElement) nameInput.value = store?.name || '';
      if (addressInput instanceof HTMLInputElement) addressInput.value = store?.addressLine || '';

      if (nameEl instanceof HTMLElement) nameEl.textContent = store?.name || '';
      if (addressEl instanceof HTMLElement) addressEl.textContent = store?.addressLine || '';
      if (panel instanceof HTMLElement) panel.hidden = !store;

      const bulky = form.dataset.cartHasBulky === 'true';
      const select = form.querySelector('[data-cart-select-store]');
      if (select instanceof HTMLElement) select.hidden = !bulky || Boolean(store);
      paintTotals(form, { pickup: Boolean(store) });
    });
  }

  /** @returns {{ postcode: string, display: string, shippingText: string } | null} */
  function readSavedEstimate() {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(SHIPPING_STORAGE_KEY) || 'null');
      if (!parsed || typeof parsed.postcode !== 'string') return null;
      const postcode = extractPostcode(parsed.postcode);
      if (!postcode) return null;
      return {
        postcode,
        display: typeof parsed.display === 'string' ? parsed.display : `${postcode}, Australia`,
        shippingText: typeof parsed.shippingText === 'string' ? parsed.shippingText : '',
      };
    } catch {
      return null;
    }
  }

  /**
   * @param {{ postcode: string, display: string, shippingText: string } | null} estimate
   */
  function writeSavedEstimate(estimate) {
    try {
      if (!estimate) {
        window.sessionStorage.removeItem(SHIPPING_STORAGE_KEY);
      } else {
        window.sessionStorage.setItem(SHIPPING_STORAGE_KEY, JSON.stringify(estimate));
      }
    } catch {
      /* ignore quota / private mode */
    }
    window.dispatchEvent(new CustomEvent('map:shipping-estimate', { detail: estimate }));
  }

  /**
   * data-* attributes are HTML-escaped in Liquid; dataset keeps the entities.
   *
   * @param {string | undefined} value
   * @param {string} fallback
   */
  function decodeDataText(value, fallback) {
    if (!value) return fallback;
    if (!value.includes('&')) return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value || fallback;
  }

  /**
   * Totals: Estimated total when C&C is set; shipping line is
   * "Click & Collect, Free" or the cheapest estimated rate.
   * Figma 10069 hides Estimate shipping once a rate (or location) is set.
   *
   * @param {HTMLElement} form
   * @param {{ pickup?: boolean, shippingText?: string }} [options]
   */
  function paintTotals(form, options = {}) {
    const pickup = options.pickup === true;
    const saved = pickup ? null : readSavedEstimate();
    const shippingText = options.shippingText || saved?.shippingText || '';
    const bulky = form.dataset.cartHasBulky === 'true';
    const label = form.querySelector('[data-cart-total-label]');
    if (label instanceof HTMLElement) {
      label.textContent = pickup
        ? decodeDataText(label.dataset.labelEstimated, 'Estimated total')
        : decodeDataText(label.dataset.labelSubtotal, 'Subtotal');
    }

    const line = form.querySelector('[data-cart-shipping-line]');
    const value = form.querySelector('[data-cart-shipping-line-value]');
    const note = form.querySelector('[data-cart-shipping-note]');
    const accordion = form.querySelector('[data-cart-shipping]');

    if (accordion instanceof HTMLElement) {
      accordion.hidden = bulky || pickup || Boolean(shippingText);
    }

    if (line instanceof HTMLElement) {
      if (pickup) {
        line.hidden = false;
        if (value) {
          value.textContent = decodeDataText(
            line.dataset.shippingClickCollect,
            'Click & Collect, Free'
          );
        }
      } else if (shippingText) {
        line.hidden = false;
        if (value) value.textContent = shippingText;
      } else {
        line.hidden = true;
      }
    }

    if (note instanceof HTMLElement) {
      if (pickup) {
        note.hidden = true;
      } else {
        note.hidden = false;
        note.textContent = shippingText
          ? decodeDataText(note.dataset.noteEstimated, 'Estimated shipping will be updated at checkout')
          : decodeDataText(note.dataset.noteCalculated, 'Shipping is calculated at checkout');
      }
    }
  }

  /** Single source in map-utils.js (WIL-461). @param {string} value */
  function extractPostcode(value) {
    return window.MapUtils.extractPostcode(value);
  }

  /** @param {string} postcode */
  function provinceFromPostcode(postcode) {
    const code = Number.parseInt(extractPostcode(postcode), 10);
    if (!Number.isFinite(code)) return '';
    if (code >= 800 && code <= 899) return 'NT';
    if (code >= 200 && code <= 299) return 'ACT';
    if (code >= 2600 && code <= 2618) return 'ACT';
    if (code >= 2900 && code <= 2920) return 'ACT';
    if (code >= 1000 && code <= 2999) return 'NSW';
    if (code >= 3000 && code <= 3999) return 'VIC';
    if (code >= 4000 && code <= 4999) return 'QLD';
    if (code >= 5000 && code <= 5999) return 'SA';
    if (code >= 6000 && code <= 6999) return 'WA';
    if (code >= 7000 && code <= 7999) return 'TAS';
    if (code >= 8000 && code <= 8999) return 'VIC';
    if (code >= 9000 && code <= 9999) return 'QLD';
    return '';
  }

  /** @param {HTMLElement} form */
  function syncShippingSubmit(form) {
    const input = form.querySelector('[data-cart-shipping-postcode]');
    const submit = form.querySelector('[data-cart-shipping-submit]');
    if (!(input instanceof HTMLInputElement) || !(submit instanceof HTMLButtonElement)) return;
    submit.disabled = !extractPostcode(input.value);
  }

  async function ensureCollectAttributes() {
    const store = readStoredCollectLocation();
    const form = document.querySelector('[data-cart-items]');
    const idInput = form instanceof HTMLElement ? form.querySelector('[data-cart-attr-id]') : null;
    const currentId = idInput instanceof HTMLInputElement ? idInput.value : '';

    paintPickupFromStore();

    if (!store) return;
    if (currentId === store.id) return;

    await syncCollectAttributes(store);
    paintPickupFromStore();
  }

  /**
   * @param {HTMLElement} form
   * @param {string} postcode
   * @param {{ silent?: boolean }} [options]
   */
  async function estimateShipping(form, postcode, options = {}) {
    const status = form.querySelector('[data-cart-shipping-status]');
    const submit = form.querySelector('[data-cart-shipping-submit]');
    const silent = options.silent === true;
    if (!silent && status instanceof HTMLElement) {
      status.hidden = false;
      status.textContent = status.dataset.calculating || 'Calculating shipping…';
    }
    if (submit instanceof HTMLButtonElement) submit.disabled = true;

    const params = new URLSearchParams({
      'shipping_address[zip]': postcode,
      'shipping_address[country]': 'Australia',
    });
    const province = provinceFromPostcode(postcode);
    if (province) params.set('shipping_address[province]', province);

    try {
      const response = await fetch(`${shopRoot()}cart/shipping_rates.json?${params.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(String(response.status));

      const data = await response.json();
      const rates = Array.isArray(data.shipping_rates) ? data.shipping_rates : [];
      if (!rates.length) {
        writeSavedEstimate(null);
        paintTotals(form, { pickup: false, shippingText: '' });
        if (status instanceof HTMLElement) {
          status.hidden = false;
          status.textContent = status.dataset.shippingNone || 'No shipping options available for this postcode.';
        }
        return;
      }

      if (status instanceof HTMLElement) status.hidden = true;

      const statusEl = status instanceof HTMLElement ? status : null;
      const cheapest = rates.slice().sort((a, b) => ratePriceCents(a.price) - ratePriceCents(b.price))[0];
      const cheapestCents = ratePriceCents(cheapest?.price);
      const shippingText =
        cheapestCents === 0 ? statusEl?.dataset.shippingFree || 'FREE' : formatMoney(cheapestCents, form);

      const postcodeInput = form.querySelector('[data-cart-shipping-postcode]');
      let display = `${postcode}, Australia`;
      if (postcodeInput instanceof HTMLInputElement) {
        if (/^\d{4}$/.test(postcodeInput.value.trim())) {
          postcodeInput.value = display;
        } else if (postcodeInput.value.trim()) {
          display = postcodeInput.value.trim();
        }
      }

      writeSavedEstimate({ postcode, display, shippingText });
      paintTotals(form, { pickup: false, shippingText });
    } catch {
      if (status instanceof HTMLElement) {
        status.hidden = false;
        status.textContent = status.dataset.shippingError || 'Unable to calculate shipping. Please try again.';
      }
    } finally {
      syncShippingSubmit(form);
    }
  }

  /** @param {HTMLElement} form */
  function formHasPickup(form) {
    const idInput = form.querySelector('[data-cart-attr-id]');
    const nameInput = form.querySelector('[data-cart-attr-name]');
    return (
      (idInput instanceof HTMLInputElement && Boolean(idInput.value)) ||
      (nameInput instanceof HTMLInputElement && Boolean(nameInput.value))
    );
  }

  /**
   * Delivery location from a prior estimate or Shopify cart address.
   * Figma 4283:10069 — if a location is set, estimate with the cheapest method.
   *
   * @returns {Promise<{ postcode: string, display: string, shippingText: string } | null>}
   */
  async function readDeliveryLocation() {
    const saved = readSavedEstimate();
    if (saved?.postcode) return saved;

    try {
      const response = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const cart = await response.json();
      const zip = extractPostcode(
        cart?.shipping_address?.zip || cart?.shipping_address?.postal_code || '',
      );
      if (!zip) return null;
      return { postcode: zip, display: `${zip}, Australia`, shippingText: '' };
    } catch {
      return null;
    }
  }

  /**
   * Cart footer states from Figma 4283:8999 annotations:
   * 1. No location, no C&C → Estimate shipping + Subtotal + checkout note
   * 2. Delivery location set → cheapest Shipping line + estimated note
   * 3. C&C set → Estimated total + Click & Collect, Free
   */
  async function restoreShippingEstimates() {
    const delivery = await readDeliveryLocation();
    document.querySelectorAll('[data-cart-items]').forEach((form) => {
      if (!(form instanceof HTMLElement)) return;
      const pickup = formHasPickup(form);
      const bulky = form.dataset.cartHasBulky === 'true';
      syncShippingSubmit(form);
      if (pickup || bulky) {
        paintTotals(form, { pickup });
        return;
      }
      if (!delivery) {
        paintTotals(form, { pickup: false });
        return;
      }

      const input = form.querySelector('[data-cart-shipping-postcode]');
      if (input instanceof HTMLInputElement && !input.value.trim()) {
        input.value = delivery.display;
      }
      if (delivery.shippingText) {
        paintTotals(form, { pickup: false, shippingText: delivery.shippingText });
      }
      estimateShipping(form, delivery.postcode, { silent: true });
    });
  }

  function openCollectDrawer() {
    const cartDrawer = getDrawer();
    if (cartDrawer?.open) {
      if (window.MapUI?.closeDrawer) window.MapUI.closeDrawer(cartDrawer, { immediate: true });
      else cartDrawer.close();
    }

    const collect = document.getElementById(COLLECT_DRAWER_ID);
    if (!(collect instanceof HTMLDialogElement)) return;

    // Store search must use the bulky cart line's part number, not whichever
    // PDP happens to be open behind the cart (click-collect.js reads this).
    const cartForm = document.querySelector('[data-cart-items][data-cart-apg-part-number]');
    const cartApg = cartForm instanceof HTMLElement ? cartForm.dataset.cartApgPartNumber || '' : '';
    if (cartApg) {
      collect.dataset.apgOverride = cartApg;
    } else {
      delete collect.dataset.apgOverride;
    }

    const trigger = document.querySelector(`[data-map-drawer-open="${COLLECT_DRAWER_ID}"]`);
    if (trigger instanceof HTMLElement) {
      trigger.click();
      return;
    }
    if (window.MapUI?.openDrawer) window.MapUI.openDrawer(collect);
    else collect.showModal();
  }

  /** @param {MouseEvent} event */
  function onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const qtyBtn = target.closest('[data-cart-qty-change]');
    if (qtyBtn instanceof HTMLElement) {
      event.preventDefault();
      const line = qtyBtn.closest('[data-cart-line]');
      const input = line?.querySelector('[data-cart-qty-input]');
      const key = line instanceof HTMLElement ? line.dataset.lineKey : '';
      if (!key || !(input instanceof HTMLInputElement)) return;

      const delta = Number.parseInt(qtyBtn.getAttribute('data-cart-qty-change') || '0', 10);
      const next = Math.max(0, (Number.parseInt(input.value, 10) || 0) + delta);
      changeLine(key, next, { openDrawer: getDrawer()?.open === true });
      return;
    }

    if (target.closest('[data-cart-remove]')) {
      event.preventDefault();
      const line = target.closest('[data-cart-line]');
      const key = line instanceof HTMLElement ? line.dataset.lineKey : '';
      if (!key) return;
      changeLine(key, 0, { openDrawer: getDrawer()?.open === true });
      return;
    }

    if (target.closest('[data-cart-shipping-submit]')) {
      const form = target.closest('[data-cart-items]');
      const postcodeInput = form?.querySelector('[data-cart-shipping-postcode]');
      if (!(form instanceof HTMLElement) || !(postcodeInput instanceof HTMLInputElement)) return;
      const postcode = extractPostcode(postcodeInput.value);
      if (!postcode) {
        const status = form.querySelector('[data-cart-shipping-status]');
        if (status instanceof HTMLElement) {
          status.hidden = false;
          status.textContent = status.dataset.shippingEmpty || 'Enter a postcode to estimate shipping.';
        }
        return;
      }
      estimateShipping(form, postcode);
      return;
    }

    if (target.closest('[data-cart-change-store]')) {
      event.preventDefault();
      openCollectDrawer();
      return;
    }

    const shopPay = target.closest('[data-cart-shop-pay]');
    if (shopPay instanceof HTMLElement) {
      event.preventDefault();
      const form = shopPay.closest('[data-cart-items]');
      const wallet = form?.querySelector(
        'shop-pay-wallet-button, shop-pay-button, shopify-accelerated-checkout-cart shop-pay-wallet-button'
      );
      if (wallet instanceof HTMLElement) {
        wallet.click();
        return;
      }
      const checkout = form?.querySelector('[name="checkout"]');
      if (checkout instanceof HTMLButtonElement) checkout.click();
    }
  }

  /** @param {KeyboardEvent} event */
  function onKeydown(event) {
    if (event.key !== 'Enter') return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-cart-shipping-postcode]')) return;
    event.preventDefault();
    const form = target.closest('[data-cart-items]');
    const postcode = extractPostcode(target.value);
    if (!(form instanceof HTMLElement)) return;
    if (!postcode) return;
    estimateShipping(form, postcode);
  }

  /** @param {Event} event */
  function onShippingInput(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-cart-shipping-postcode]')) return;
    const form = target.closest('[data-cart-items]');
    if (form instanceof HTMLElement) syncShippingSubmit(form);
  }

  /** @param {CustomEvent<{ openDrawer?: boolean, itemCount?: number }>} event */
  function onCartUpdated(event) {
    const itemCount = event.detail?.itemCount;
    refreshCartViews(typeof itemCount === 'number' ? itemCount : undefined, {
      openDrawer: Boolean(event.detail?.openDrawer),
    });
  }

  function onCollectSelected() {
    // click-collect.js owns the cart-attribute write (it POSTs before
    // dispatching this event) — re-POSTing here raced that request.
    paintPickupFromStore();
    restoreShippingEstimates();
  }

  function initCart() {
    const dialog = getDrawer();
    if (dialog) bindCloseButtons(dialog);
    if (document.documentElement.dataset.cartAjaxBound === 'true') {
      ensureCollectAttributes().then(() => restoreShippingEstimates());
      return;
    }
    document.documentElement.dataset.cartAjaxBound = 'true';
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('input', onShippingInput);
    document.addEventListener('map:cart:updated', onCartUpdated);
    window.addEventListener('map:collect-location-selected', onCollectSelected);
    document.querySelectorAll('[data-cart-items]').forEach((form) => {
      if (form instanceof HTMLElement) syncShippingSubmit(form);
    });
    ensureCollectAttributes().then(() => restoreShippingEstimates());
  }

  /** @type {Window & { MAPCartDrawer?: Record<string, unknown> }} */
  const win = window;

  win.MAPCartDrawer = {
    refresh: refreshCartDrawer,
    refreshAll: refreshCartViews,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCart);
  } else {
    initCart();
  }
})();
